import React, { useCallback, useState } from "react";
import { Modal, Button, Alert } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useCreate, useInvalidate } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { useForm } from "@refinedev/antd";
import { DynamicForm } from "./index";
import { useTocLabels } from "../../contexts/TocLabelContext";

interface RelatedItemCreateModalProps {
  /** Aras ItemType name (e.g., "Contract", "Organization") */
  itemTypeName: string;
  /** API resource name (e.g., "contracts", "organizations") */
  resourceName: string;
  /** Whether the modal is visible */
  open: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when item is successfully created */
  onSuccess?: (newItem: { id: string; name?: string }) => void;
}

/**
 * Translate ItemType name for UI display
 * Uses hybrid approach: translation → TOC label → original name
 */
const translateItemType = (
  itemType: string | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
  language: string,
  getLabel?: (itemTypeName: string, language: string) => string | undefined,
): string => {
  if (!itemType) return "";
  // Try to get translation from navigation namespace
  const translationKey = `navigation.${itemType}`;
  const translated = t(translationKey);
  // If translation key is found (not returned as-is), use it
  if (translated !== translationKey) {
    return translated;
  }
  // Fall back to TOC menu label from Aras
  if (getLabel) {
    const tocLabel = getLabel(itemType, language);
    if (tocLabel) {
      return tocLabel;
    }
  }
  // Final fallback: use original name
  return itemType;
};

/**
 * Gets the resource name from an item type.
 * Uses exact ItemType name - no conversion.
 */
export const getResourceFromItemType = (itemType: string): string => {
  return itemType;
};

/**
 * Modal component for creating related items inline.
 * Uses DynamicForm to render form fields based on Aras metadata and Responsive Form layout.
 */
export const RelatedItemCreateModal: React.FC<RelatedItemCreateModalProps> = ({
  itemTypeName,
  resourceName,
  open,
  onClose,
  onSuccess,
}) => {
  const { t, i18n } = useTranslation();
  const { getLabel } = useTocLabels();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formStatus, setFormStatus] = useState<{
    isConfigured: boolean;
    isLoading: boolean;
  }>({ isConfigured: true, isLoading: true });
  const invalidate = useInvalidate();

  // Form instance from Refine
  const { formProps, form } = useForm({
    resource: resourceName,
    action: "create",
    redirect: false,
  });

  // Create mutation
  const { mutateAsync: createItem, isLoading: isCreating } = useCreate();

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    try {
      setSubmitError(null);
      const values = await form.validateFields();

      // Create the item
      const result = await createItem({
        resource: resourceName,
        values,
      });

      // Invalidate related items cache to trigger refetch
      await invalidate({
        resource: resourceName,
        invalidates: ["list"],
      });

      // Call success callback with new item data
      if (onSuccess && result.data) {
        const newItem = result.data as { id: string; name?: string };
        onSuccess({
          id: newItem.id,
          name: newItem.name,
        });
      }

      // Reset form and close modal
      form.resetFields();
      onClose();
    } catch (error) {
      if (error instanceof Error) {
        setSubmitError(error.message);
      } else if (typeof error === "object" && error !== null) {
        // Handle Ant Design form validation errors
        const validationError = error as { errorFields?: unknown[] };
        if (!validationError.errorFields) {
          setSubmitError(t("dynamicForm.createError"));
        }
      }
    }
  }, [form, createItem, resourceName, invalidate, onSuccess, onClose, t]);

  // Handle modal close
  const handleClose = useCallback(() => {
    form.resetFields();
    setSubmitError(null);
    onClose();
  }, [form, onClose]);

  // Handle form status changes from DynamicForm
  const handleFormStatusChange = useCallback(
    (status: { isConfigured: boolean; isLoading: boolean }) => {
      setFormStatus(status);
    },
    [],
  );

  // Hide create button when form is not configured
  const shouldShowCreateButton =
    formStatus.isConfigured && !formStatus.isLoading;

  return (
    <Modal
      title={
        <span>
          <PlusOutlined style={{ marginRight: 8 }} />
          {t("dynamicForm.createNew", {
            itemType: translateItemType(
              itemTypeName,
              t,
              i18n.language,
              getLabel,
            ),
          })}
        </span>
      }
      open={open}
      onCancel={handleClose}
      width={800}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          {t("buttons.cancel")}
        </Button>,
        shouldShowCreateButton && (
          <Button
            key="submit"
            type="primary"
            loading={isCreating}
            onClick={handleSubmit}
            icon={<PlusOutlined />}
          >
            {t("buttons.create")}
          </Button>
        ),
      ]}
      destroyOnClose
    >
      {submitError && (
        <Alert
          message={t("dynamicForm.createError")}
          description={submitError}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setSubmitError(null)}
        />
      )}

      {/* Use DynamicForm for consistent Aras Responsive Form layout */}
      <DynamicForm
        itemTypeName={itemTypeName}
        mode="create"
        formProps={{
          ...formProps,
          onFinish: handleSubmit,
        }}
        form={form}
        notesPlacement="none"
        onFormStatusChange={handleFormStatusChange}
      />
    </Modal>
  );
};
